import LocaleKeys from '../../locales/keys';
import i18next from 'i18next';
import {
  getCurrencyDetails,
  getIapPaymentInstrumentType,
} from '../../utils/common';
import { formatDateString, getFreeTrialImage } from '../../utils/common';
import { getStripePaymentList } from '../../utils/stripeUtility';
import { STRIPE } from '../../constants/stripePayment.constant';
import { getEligibilityAction, getPlatformName } from './mySubscriptionHelpers';

const getDynamicText = (key, interpolationNode = {}) =>
  i18next.t(key, interpolationNode);

const getIsAffiliatePlan = (subscriptionNode, matchedText) =>
  (
    subscriptionNode?.Items?.[0]?.PricingPlan?.SubscriptionBillingCycleName ||
    ''
  )
    ?.toLowerCase()
    .includes(matchedText);

const getIap = (subscriptionNode, isAffiliateplanNode, matchedText) => {
  if (isAffiliateplanNode) {
    return false;
  }
  if (
    subscriptionNode?.PaymentInstrument?.Type === 22 &&
    subscriptionNode?.PaymentInstrument?.TypeName === 'Stripe Account'
  ) {
    return false;
  }
  return (
    (subscriptionNode?.Items?.[0]?.PricingPlan?.Name || '')
      .toLowerCase()
      .includes(matchedText) ||
    getIapPaymentInstrumentType(subscriptionNode?.PaymentInstrument?.Type)
  );
};

const getAffiliatePlanList = (subscriptionNode, isAffiliateplanNode) => {
  if (!isAffiliateplanNode) return null;
  return {
    imageUrl: subscriptionNode?.Items?.[0]?.PricingPlan?.ImageUrl || '',
    nextPaymentDateText: 'N/A*',
    paymentMethodDescriptionText:
      subscriptionNode?.Items?.[0]?.PricingPlan?.Description || '',
  };
};

const getIapList = (isIapNode) => {
  if (!isIapNode) return null;
  return {
    paymentMethodHeader: i18next.t(
      LocaleKeys.subscriptionInfo.subscriptions.appStoreHeader,
    ),
    paymentMethodDescriptionText: i18next.t(
      LocaleKeys.subscriptionInfo.subscriptions.appStoreText,
    ),
  };
};

export const getSubscriptionDate = (subscriptionDateNode) => {
  if (!subscriptionDateNode) {
    return '';
  }
  return formatDateString(subscriptionDateNode);
};

export const subscriptionProperties = (
  subscriptionUiNode,
  stripePayment,
  entitledPlans = [],
) => {
  if (!subscriptionUiNode?.Items) {
    return {};
  }
  const isITunes = !!subscriptionUiNode?.PaymentInstrument?.ITunesAccount;
  const isCancelable =
    subscriptionUiNode.Status === 1 &&
    subscriptionUiNode.Renew === true &&
    !isITunes;
  const changePricingPlan = subscriptionUiNode?.Items?.[0]?.ChangePricingPlan;
  const renewable =
    subscriptionUiNode.Status === 1 &&
    subscriptionUiNode.Renew === false &&
    subscriptionUiNode.Policy.Renewable &&
    !isITunes;
  const subscriptionStartDate = getSubscriptionDate(
    subscriptionUiNode.SubscriptionStart,
  );
  const isSubscriptionRenew = subscriptionUiNode.Renew;
  const cycleDate = getSubscriptionDate(subscriptionUiNode.CycleDate);

  const isAffiliateplan = getIsAffiliatePlan(
    subscriptionUiNode,
    'affiliateplan',
  );
  const affiliateplanList = getAffiliatePlanList(
    subscriptionUiNode,
    isAffiliateplan,
  );
  const getStripeConfig = getStripePaymentList(
    subscriptionUiNode?.PaymentInstrument,
    stripePayment,
  );
  const platformName = getPlatformName(subscriptionUiNode, entitledPlans);
  const isIap = getIap(subscriptionUiNode, isAffiliateplan, 'iap');
  const iapList = getIapList(isIap);
  const isStripeAccount = getStripeConfig?.type === STRIPE;
  const isSelectedStripePaymentType = getStripeConfig?.accountType;
  const currentPlan = subscriptionUiNode?.Items?.[0]?.PricingPlan?.Name;
  const currentPackage = subscriptionUiNode?.Items?.[0]?.Product?.Name;
  const pricingPlanCurrency =
    subscriptionUiNode?.Items?.[0]?.PricingPlan?.Currency;
  const pricingPlanChargeAmount =
    subscriptionUiNode?.Items?.[0]?.PricingPlan?.ChargeAmount;
  const isCreditCardToStripe = getStripeConfig?.isCreditCard;
  const subscriptionId = subscriptionUiNode?.Id;
  const eligibilityAction = getEligibilityAction(
    subscriptionUiNode,
    entitledPlans,
  );
  return {
    isCancelable,
    changePricingPlan,
    renewable,
    subscriptionStartDate,
    cycleDate,
    isAffiliateplan,
    affiliateplanList,
    isIap,
    iapList,
    isStripeAccount,
    isSelectedStripePaymentType,
    pricingPlanCurrency,
    pricingPlanChargeAmount,
    isCreditCardToStripe,
    currentPlan,
    currentPackage,
    platformName,
    subscriptionId,
    eligibilityAction,
    isSubscriptionRenew,
  };
};

export const isFreeTrialPaymentText = (subscriptionNode) => {
  const subscriptionAmountNoteLabel = getDynamicText(
    LocaleKeys.subscriptionInfo.subscriptions.subscriptionAmountNoteLabel,
  );
  const subscriptionAmountNoteText = getDynamicText(
    LocaleKeys.subscriptionInfo.subscriptions.subscriptionAmountNote,
    {
      startDate: subscriptionNode?.subscriptionStartDate,
      cycleDate: subscriptionNode?.cycleDate,
    },
  );
  const subscriptionFirstPaymentNoteText = getDynamicText(
    LocaleKeys.subscriptionInfo.subscriptions.subscriptionTrialNote,
    {
      pricingPlanName: subscriptionNode?.changePricingPlan?.Name,
      cycleDate: subscriptionNode?.cycleDate,
      chargeWithCurrency: getCurrencyDetails(
        subscriptionNode?.changePricingPlan?.RenewalChargeAmount,
        subscriptionNode?.changePricingPlan?.Currency,
      )?.priceWithCurrencySymbol,
    },
  );
  return {
    subscriptionAmountNoteLabel,
    subscriptionAmountNoteText,
    subscriptionFirstPaymentNoteText,
  };
};

export const getPlanImageList = (
  isFreeTrial,
  subscriptionPropsNode,
  classOptions,
) => {
  if (isFreeTrial) {
    return {
      imageUrl: getFreeTrialImage(),
      altText: 'Seven Day Free Trial',
      isImageShown: true,
      classOptions: classOptions.freeTrial,
    };
  }
  if (subscriptionPropsNode?.isAffiliateplan) {
    return {
      imageUrl: subscriptionPropsNode?.affiliateplanList?.imageUrl,
      altText: 'Affiliate Partnership Logo',
      isImageShown: true,
      classOptions: classOptions.affiliate,
    };
  }
  return {
    isImageShown: false,
  };
};

export const getNextPaymentDateText = (
  subscriptionPropertiesUiNode,
  subscriptionUiNode,
) => {
  if (subscriptionPropertiesUiNode.isAffiliateplan) {
    return subscriptionPropertiesUiNode?.affiliateplanList?.nextPaymentDateText;
  }
  return getSubscriptionDate(
    subscriptionUiNode?.details?.CycleDate,
    'MM/DD/YYYY',
  );
};

export const getPaymentMethodDescriptionTextNode = (subscriptionPropsNode) => {
  if (subscriptionPropsNode.isAffiliateplan) {
    return {
      descriptionText:
        subscriptionPropsNode?.affiliateplanList?.paymentMethodDescriptionText,
      headerText: i18next.t(
        LocaleKeys.subscriptionInfo.subscriptions.paymentHeader,
      ),
    };
  }

  if (subscriptionPropsNode.isIap) {
    return {
      descriptionText:
        subscriptionPropsNode?.iapList?.paymentMethodDescriptionText,
      headerText: subscriptionPropsNode?.iapList?.paymentMethodHeader,
    };
  }
  return {
    headerText: i18next.t(
      LocaleKeys.subscriptionInfo.subscriptions.paymentHeader,
    ),
  };
};
